import React from "react";
import { Reveal, Marquee } from "./Motion";
import { Clock, MapPin, Phone, Mail } from "lucide-react";
import { BUSINESS } from "../config";

const CHAPTERS = [
  { n: "01", t: "Local & owner-run", d: `Since ${BUSINESS.founded}, ${BUSINESS.name} has cleared homes, garages, job sites, and estates across ${BUSINESS.city}. No call centers — you talk to the crew that shows up.` },
  { n: "02", t: "We do the lifting", d: "Point at it and it's gone. We handle the heavy, awkward, and up-three-flights-of-stairs stuff so you never touch a thing." },
  { n: "03", t: "We resell to save you money", d: "If your junk is resellable, we discount your haul price for it. Less to the landfill, more back in your pocket." },
];

export default function Manifesto() {
  return (
    <section id="about" data-testid="about-section" className="relative bg-[#050505] py-24 md:py-36">
      <div className="border-y border-[var(--border-dim)] py-4 mb-20">
        <Marquee items={["Junk Removal", "Estate Cleanouts", "Appliance Haul", "Construction Debris", "Resale & Donations", "Same-Day Bids"]} className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight text-[var(--text-primary)]" />
      </div>

      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <Reveal className="mb-16 max-w-3xl">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--accent)]">/ Who we are</span>
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase tracking-tight mt-4">A cleaner way to clear out.</h2>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-px bg-[var(--border-dim)] border border-[var(--border-dim)]">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.1} className="bg-[#0a0a0a] p-8 md:p-10 hover:bg-[#101010] transition-colors duration-500 group">
              <div className="flex items-baseline justify-between mb-8">
                <span className="font-mono text-sm text-[var(--text-secondary)]">CH. {c.n}</span>
                <span className="h-2 w-2 bg-[var(--accent)] group-hover:scale-150 transition-transform" />
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-bold uppercase mb-4">{c.t}</h3>
              <p className="text-[var(--text-secondary)] leading-relaxed">{c.d}</p>
            </Reveal>
          ))}
        </div>

        {/* Info + hours + contact */}
        <div className="grid md:grid-cols-3 gap-px bg-[var(--border-dim)] border-x border-b border-[var(--border-dim)]">
          <div data-testid="hours-block" className="bg-[#0a0a0a] p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6 text-[var(--accent)]"><Clock size={18} /><span className="font-mono text-xs uppercase tracking-[0.2em]">Hours</span></div>
            {BUSINESS.hours.map((h) => (
              <div key={h.d} className="flex justify-between py-2 border-b border-[var(--border-dim)] font-mono text-sm">
                <span className="text-[var(--text-secondary)]">{h.d}</span><span>{h.h}</span>
              </div>
            ))}
          </div>
          <div className="bg-[#0a0a0a] p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6 text-[var(--accent)]"><MapPin size={18} /><span className="font-mono text-xs uppercase tracking-[0.2em]">Service Area</span></div>
            <p className="text-2xl font-display font-bold uppercase leading-tight">{BUSINESS.city}</p>
            <p className="text-[var(--text-secondary)] mt-2">{BUSINESS.serviceArea}</p>
            <p className="font-mono text-xs text-[var(--text-secondary)] mt-6">{BUSINESS.domain}</p>
          </div>
          <div data-testid="contact-block" className="bg-[#0a0a0a] p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6 text-[var(--accent)]"><Phone size={18} /><span className="font-mono text-xs uppercase tracking-[0.2em]">Contact</span></div>
            <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="contact-phone" className="block font-display text-2xl font-bold hover:text-[var(--accent)] transition-colors">{BUSINESS.phone}</a>
            <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email" className="inline-flex items-center gap-2 text-[var(--text-secondary)] hover:text-white transition-colors mt-3 font-mono text-sm"><Mail size={14} /> {BUSINESS.email}</a>
          </div>
        </div>
      </div>
    </section>
  );
}
